/**
 * 챌린지 편집기 — 챌린지 머리 정보와 문항(퀴즈/과제)을 한 벌로 등록·수정한다(2026-10-05).
 *
 * 편집기는 **교환 파일 형식**(backend/modules/challenge/exchange.js, camelCase)으로 읽고 쓴다. 그래서 저장할 곳이
 * 셋이어도 형식은 하나다 — JSON 파일(내보내기와 같음) · 가져온 것(가져오기와 같은 경로) · QBase(머리 정보만, LSADMIN).
 *
 * 지킬 것
 *  - **모르는 필드를 버리지 않는다.** 문항·챌린지의 원래 객체(raw)를 깔고 편집한 칸만 덮는다. 튜닝 문항의 채점 설정
 *    (tolerance·partialFactor·thresholds 등 화면에 없는 키)도 유형이 그대로면 남긴다.
 *  - **문항 번호는 저장할 때 1..n 으로 다시 매긴다**(화면이 슬롯을 번호로 그린다 — 중간이 비면 그 뒤가 안 보인다).
 *  - **QBase 에는 머리 정보만, 바뀐 칸만** 보낸다(saveCmm updatedRows 는 보낸 칸만 UPDATE — 다 보내면 남이 바꾼 칸을
 *    덮는다). 문항 저장 API 는 아직 없다 — 문항이 바뀌었으면 QBase 저장을 막는다(머리만 가고 문항이 조용히 남으면 안 된다).
 */
import { CHALLENGE_KINDS, CHALLENGE_DBMS, parseChallengeAttr } from './challengeAttr';

export const LEVEL_CODES = ['01', '02', '03'];
export const QUIZ_TYPES = ['001', '002', '003', '004'];
/** 종류별로 넣을 수 있는 문항 유형 — 쿼리챌린지에 튜닝 실습이 섞이면 쿼리챌린지 목록에서 튜닝 과제가 보인다 */
export const QUIZ_TYPES_BY_KIND = { query: ['001', '002'], tuning: ['003', '004', '002'] };
/** 화면이 그리는 문항 슬롯 수(QueryChallenge TOTAL_QUIZ_COUNT) — 넘으면 풀 수 없는 문항이 생긴다 */
export const MAX_QUIZZES = 10;
export const SCOPES = ['sql', 'sql+index'];
/**
 * 챌린지 상태 — 작성중 / 공개 / 비공개(2026-10-06). 작성중은 속성 `status:"draft"` + `publicYn='N'` 으로 담는다
 * (상태를 모르는 옛 빌드에서도 적어도 비공개로 읽히게). 새 챌린지는 작성중으로 시작한다.
 */
export const STATUSES = ['draft', 'open', 'closed'];

/** 로그인 역할 코드 목록(콤마, 대문자)에 LSADMIN 이 있는가 — QBase 챌린지 저장 API 의 역할. 표시 편의일 뿐, 권위는 서버의 403 */
export function hasLsAdminRole(roleCd) {
  return String(roleCd || '').toUpperCase().split(',').map(s => s.trim()).includes('LSADMIN');
}

/** 'YYYYMMDD' 또는 'YYYYMMDDhhmmss' → date 입력값 'YYYY-MM-DD'. 못 읽으면 '' */
export function toInputDate(v) {
  const m = /^(\d{4})(\d{2})(\d{2})/.exec(String(v || ''));
  return m ? `${m[1]}-${m[2]}-${m[3]}` : '';
}

/**
 * date 입력값 → 저장 값. **원래 길이를 따른다** — QBase 는 14자리(시각 포함, 원래 시각 보존), 샘플·교환 파일은 8자리.
 * 원래 값이 없으면 8자리. 비우면 ''.
 */
export function fromInputDate(input, orig) {
  if (!input) return '';
  const day = input.replace(/-/g, '');
  const o = String(orig || '');
  return o.length === 14 ? day + o.slice(8) : day;
}

const parseObj = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'object') return { ...raw };
  try {
    const v = JSON.parse(String(raw));
    return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
  } catch (_e) {
    return {};
  }
};

let seq = 0;
const nextKey = () => `q${Date.now().toString(36)}${(seq += 1)}`;

// ── 머리 정보 ─────────────────────────────────────────────────────────────

/** 새 챌린지 번호 — 로컬 저장소에서 겹치지 않게 시각으로 짓는다(사용자가 바꿀 수 있다) */
export function newChallengeId(now = new Date()) {
  const p = (n) => String(n).padStart(2, '0');
  return `LC${now.getFullYear()}${p(now.getMonth() + 1)}${p(now.getDate())}${p(now.getHours())}${p(now.getMinutes())}${p(now.getSeconds())}`;
}

/** 교환 형식의 챌린지 → 머리 정보 양식. 없으면 새 챌린지 양식 */
export function headerForm(c, defaults = {}) {
  const attr = parseChallengeAttr(c?.challengeAttrCn);
  return {
    challengeId: c?.challengeId ? String(c.challengeId) : (defaults.challengeId || ''),
    author: defaults.author || null,   // 새 챌린지의 작성자(mgrUsrid) — 있는 챌린지는 원래 값을 그대로 둔다
    challengeNm: c?.challengeNm || '',
    bgngDt: toInputDate(c?.bgngDt),
    endDt: toInputDate(c?.endDt),
    challengeLevelDcd: LEVEL_CODES.includes(String(c?.challengeLevelDcd)) ? String(c.challengeLevelDcd) : '01',
    status: c ? (attr.status === 'draft' ? 'draft' : (c.publicYn === 'N' ? 'closed' : 'open')) : 'draft',
    kind: CHALLENGE_KINDS.includes(attr.kind) ? attr.kind : (defaults.kind || 'query'),
    dbms: CHALLENGE_DBMS.includes(attr.dbms) ? attr.dbms : (defaults.dbms || 'oracle'),
  };
}

/** 머리 정보 검사 — 오류 키(challenge:edit.err.*) 또는 null */
export function validateHeader(f, { isNew = false, existingIds = [] } = {}) {
  if (isNew) {
    if (!/^[A-Za-z0-9_-]{1,40}$/.test(f.challengeId || '')) return 'id';
    if (existingIds.map(String).includes(String(f.challengeId))) return 'idTaken';
  }
  if (!String(f.challengeNm || '').trim()) return 'nameRequired';
  if (f.bgngDt && f.endDt && f.bgngDt > f.endDt) return 'dateOrder';
  if (!LEVEL_CODES.includes(f.challengeLevelDcd)) return 'level';
  if (!STATUSES.includes(f.status)) return 'status';
  if (!CHALLENGE_KINDS.includes(f.kind) || !CHALLENGE_DBMS.includes(f.dbms)) return 'attr';
  return null;
}

/** 양식 + 원래 챌린지 → 교환 형식의 챌린지(문항 제외). 원래 객체의 모르는 필드는 남긴다 */
export function headerToExchange(f, orig) {
  const out = { ...(orig || {}) };
  // 내보내기가 깔아 둔 옛 이름(QBase 원래 칸·리덕스 칸)은 뺀다 — 다시 읽는 쪽이 옛 이름을 먼저 보면 고친 값이 묻힌다
  for (const k of ['QRY_CHALLENGE_ID', 'qryChallengeId', 'CHALLENGE_NM', 'qryChallengeNm', 'CHALLENGE_LEVEL_DCD',
    'BGNG_DT', 'startDt', 'END_DT', 'PUBLIC_YN', 'openYn', 'CHALLENGE_ATTR_CN', 'quizzes']) delete out[k];
  const attr = parseObj(orig?.challengeAttrCn);
  delete attr.status;
  if (f.status === 'draft') attr.status = 'draft';
  return {
    ...out,
    // 작성자 — 새 챌린지는 지금 사용자. 웹 가져오기는 작성중 챌린지의 작성자를 가져온 사람으로 다시 확정한다(백엔드)
    mgrUsrid: out.mgrUsrid ?? f.author ?? null,
    challengeId: String(f.challengeId),
    challengeNm: String(f.challengeNm).trim(),
    challengeLevelDcd: f.challengeLevelDcd,
    bgngDt: fromInputDate(f.bgngDt, orig?.bgngDt) || null,
    endDt: fromInputDate(f.endDt, orig?.endDt) || null,
    publicYn: f.status === 'open' ? 'Y' : 'N',
    challengeAttrCn: JSON.stringify({ ...attr, v: attr.v ?? 1, kind: f.kind, dbms: f.dbms }),
  };
}

/**
 * QBase 저장용 — 머리 정보 중 **바뀐 칸만**(camelCase). 아무것도 안 바뀌었으면 빈 객체.
 * QBase 는 null 로 칸을 못 비우므로 빈 날짜는 ''.
 */
export function headerPatch(orig, f) {
  const next = headerToExchange(f, orig);
  const patch = {};
  for (const k of ['challengeNm', 'challengeLevelDcd', 'publicYn']) {
    if (String(next[k] ?? '') !== String(orig?.[k] ?? '')) patch[k] = next[k];
  }
  for (const k of ['bgngDt', 'endDt']) {
    if (String(next[k] ?? '') !== String(orig?.[k] ?? '')) patch[k] = next[k] ?? '';
  }
  const a = parseObj(orig?.challengeAttrCn);
  if (a.kind !== f.kind || a.dbms !== f.dbms || (a.status === 'draft') !== (f.status === 'draft')) {
    patch.challengeAttrCn = next.challengeAttrCn;
  }
  return patch;
}

// ── 문항 ───────────────────────────────────────────────────────────────────

const defaultChoices = () => ['a', 'b', 'c', 'd'].map(id => ({ id, text: '', isAnswer: false }));

/** 교환 형식의 문항 → 편집 모델 */
export function quizModel(q) {
  const tcd = QUIZ_TYPES.includes(String(q?.quizTcd)) ? String(q.quizTcd) : '001';
  const spec = tcd === '001' ? {} : parseObj(q?.cransRstCn);
  const idx = Array.isArray(spec.answerIndexes) && spec.answerIndexes.length
    ? spec.answerIndexes
    : (Array.isArray(spec.answerColumns) && spec.answerColumns.length ? [spec.answerColumns] : []);
  return {
    key: nextKey(),
    raw: q || {},
    quizTcd: tcd,
    quizDc: q?.quizDc || '',
    quizHint: q?.quizHint || '',
    cransSqlCn: q?.cransSqlCn || '',
    choices: Array.isArray(spec.choices)
      ? spec.choices.map(c => ({ id: String(c.id), text: c.text || '', isAnswer: c.isAnswer === 'Y' || c.isAnswer === true }))
      : (tcd === '002' ? defaultChoices() : []),
    slowSql: spec.slowSql || '',
    probeSql: spec.probeSql || '',
    targetTable: spec.targetTable || '',
    answerIndexesText: idx.map(cols => cols.join(', ')).join('\n'),
    answerUnique: !!spec.answerUnique,
    scope: SCOPES.includes(spec.scope) ? spec.scope : (tcd === '004' ? 'sql+index' : 'sql'),
    baselineBuffers: spec.baselineBuffers == null ? '' : String(spec.baselineBuffers),
  };
}

/** 새 문항 — 유형만 정해 둔다 */
export const newQuiz = (tcd) => quizModel({ quizTcd: tcd });

/** 유형을 바꾼다 — 지문·힌트는 남기고, 객관식으로 바뀌는데 보기가 없으면 빈 보기 넷 */
export function changeQuizType(m, tcd) {
  const next = { ...m, quizTcd: tcd };
  if (tcd === '002' && !next.choices.length) next.choices = defaultChoices();
  if (tcd === '004' && m.quizTcd !== '004' && m.scope === 'sql') next.scope = 'sql+index';
  return next;
}

/** 다음 보기 id — a, b, c … (지운 뒤에도 겹치지 않게 쓰인 것 다음 글자) */
export function nextChoiceId(choices) {
  const used = new Set(choices.map(c => c.id));
  for (let i = 0; i < 26; i += 1) {
    const id = String.fromCharCode(97 + i);
    if (!used.has(id)) return id;
  }
  return `c${choices.length + 1}`;
}

const IDENT = /^[\p{L}_][\p{L}\p{N}_$#]*$/u;

/** 「PROD_ID, TIME_ID」 줄마다 인덱스 하나 → [[컬럼…]…]. 못 읽는 이름이 있으면 null */
export function parseIndexLines(text) {
  const out = [];
  for (const line of String(text || '').split(/\r?\n/)) {
    const cols = line.split(',').map(s => s.trim()).filter(Boolean);
    if (!cols.length) continue;
    if (!cols.every(c => IDENT.test(c))) return null;
    out.push(cols.map(c => c.toUpperCase()));
  }
  return out;
}

/** 문항 검사 — 오류 키(challenge:edit.qerr.*) 또는 null */
export function validateQuiz(m) {
  if (!m.quizDc.trim()) return 'quizDc';
  if (m.baselineBuffers !== '' && !/^\d+$/.test(String(m.baselineBuffers).trim())) return 'baseline';
  switch (m.quizTcd) {
    case '001':
      return m.cransSqlCn.trim() ? null : 'answerSql';
    case '002':
      if (m.choices.length < 2) return 'choicesMin';
      if (m.choices.some(c => !c.text.trim())) return 'choiceText';
      return m.choices.filter(c => c.isAnswer).length === 1 ? null : 'choiceAnswer';
    case '003':
      if (!m.slowSql.trim()) return 'slowSql';
      return m.cransSqlCn.trim() ? null : 'answerSql';
    case '004': {
      if (!m.probeSql.trim()) return 'probeSql';
      if (!IDENT.test(m.targetTable.trim())) return 'targetTable';
      const idx = parseIndexLines(m.answerIndexesText);
      if (!idx) return 'indexName';
      return idx.length ? null : 'answerIndex';
    }
    default:
      return 'type';
  }
}

/** 편집 모델 → 교환 형식의 문항. 번호는 부르는 쪽 순서(1..n), 튜닝 설정의 DBMS 는 챌린지의 것 */
export function quizToExchange(m, index, dbms) {
  const raw = { ...m.raw };
  for (const k of ['QUIZ_NO', 'QUIZ_TCD', 'QUIZ_DC', 'QUIZ_HINT', 'CRANS_SQL_CN', 'CRANS_RST_CN']) delete raw[k];
  const sameType = String(m.raw?.quizTcd || '') === m.quizTcd;
  // 화면에 없는 채점 설정 키(tolerance 등)는 유형이 같을 때만 이어받는다 — 다른 유형의 설정은 뜻이 없다
  const keep = sameType ? parseObj(m.raw?.cransRstCn) : {};
  const baseline = String(m.baselineBuffers).trim() === '' ? null : Number(m.baselineBuffers);
  const out = {
    ...raw,
    quizNo: index + 1,
    quizTcd: m.quizTcd,
    quizDc: m.quizDc,
    quizHint: m.quizHint.trim() ? m.quizHint : null,
  };
  switch (m.quizTcd) {
    case '001':
      return { ...out, cransSqlCn: m.cransSqlCn, cransRstCn: sameType ? (m.raw.cransRstCn ?? null) : null };
    case '002':
      return {
        ...out,
        cransSqlCn: null,
        cransRstCn: JSON.stringify({
          ...keep,
          choices: m.choices.map(c => ({ id: c.id, text: c.text, isAnswer: c.isAnswer ? 'Y' : 'N' })),
        }),
      };
    case '003':
      return {
        ...out,
        cransSqlCn: m.cransSqlCn,
        cransRstCn: JSON.stringify({
          tolerance: null, partialFactor: null, thresholds: null,
          ...keep,
          slowSql: m.slowSql, scope: m.scope, baselineBuffers: baseline, dbms,
        }),
      };
    case '004': {
      const idx = parseIndexLines(m.answerIndexesText) || [];
      return {
        ...out,
        cransSqlCn: m.cransSqlCn.trim() ? m.cransSqlCn : null,
        cransRstCn: JSON.stringify({
          tolerance: null, partialFactor: null, thresholds: null,
          ...keep,
          probeSql: m.probeSql,
          targetTable: m.targetTable.trim().toUpperCase(),
          answerColumns: idx[0] || [],
          answerUnique: !!m.answerUnique,
          // 모범 인덱스가 여럿일 때만(OR 문항). 하나면 answerColumns 로 충분하다(생성기와 같은 모양)
          answerIndexes: idx.length > 1 ? idx : null,
          scope: m.scope, baselineBuffers: baseline, dbms,
        }),
      };
    }
    default:
      return out;
  }
}

/**
 * 클라우드 날짜는 14자리(YYYYMMDDhhmmss)다 — 새 챌린지의 8자리 날짜를 시작 00:00:00 · 끝 23:59:59 로 늘린다.
 * 이미 14자리면 그대로(원래 시각 보존 — fromInputDate 가 맞춰 둔다).
 */
export function qbaseDate(v, which) {
  const s = String(v || '');
  if (s.length === 8) return s + (which === 'end' ? '235959' : '000000');
  return s;
}

/** 클라우드 칸(varchar 4000)을 넘는 문항 칸 — [{ quizNo, field }]. 비면 통과 */
export function qbaseTooLong(quizzes, limit = 4000) {
  const out = [];
  for (const q of quizzes || []) {
    for (const k of ['quizDc', 'quizHint', 'cransSqlCn', 'cransRstCn', 'addCransVerifCn', 'atchFileLstCn']) {
      if (q[k] != null && String(q[k]).length > limit) out.push({ quizNo: q.quizNo, field: k });
    }
  }
  return out;
}

// ── 튜닝 과제 사전 검증(2026-10-06) ──────────────────────────────────────────

export const isTuningQuiz = (m) => m.quizTcd === '003' || m.quizTcd === '004';

/** 검증 요청 모양(backend challenge/taskVerify.normalizeTask 가 받는 것). 기준 블록은 표시값이라 넣지 않는다 */
export function taskOfModel(m) {
  return {
    quizTcd: m.quizTcd,
    scope: m.scope,
    slowSql: m.quizTcd === '003' ? m.slowSql : undefined,
    probeSql: m.quizTcd === '004' ? m.probeSql : undefined,
    answerSql: m.cransSqlCn,
    targetTable: m.quizTcd === '004' ? m.targetTable.trim().toUpperCase() : undefined,
    answerIndexes: parseIndexLines(m.answerIndexesText) || [],
    answerUnique: !!m.answerUnique,
  };
}

/** 검증 결과가 어느 내용에 대한 것인지 — 채점에 쓰이는 칸과 DBMS 가 하나라도 바뀌면 다시 검증해야 한다 */
export const taskSignature = (m, dbms) => JSON.stringify({ dbms, ...taskOfModel(m) });

/**
 * 저장 전에 검증이 필요한데 통과하지 못한 튜닝 과제의 순번(0부터).
 * 처음 연 그대로인 과제(initialSigs 에 있는 것)는 묻지 않는다 — 이미 쓰이던 과제를 손대지 않았는데 막을 이유가 없다.
 * @param verifs  { [signature]: { status: 'ok' | 'fail' | 'error' | 'running' } }
 */
export function unverifiedTuning(models, dbms, initialSigs, verifs) {
  const out = [];
  models.forEach((m, i) => {
    if (!isTuningQuiz(m)) return;
    const sig = taskSignature(m, dbms);
    if (initialSigs && initialSigs.has(sig)) return;
    if (!verifs[sig] || verifs[sig].status !== 'ok') out.push(i);
  });
  return out;
}

// ── 검토 결과 반영(2026-10-06) — 본인 LLM 검토와 공용 LLM 판정의 「보완」 이 같은 모양이다 ──

/** 검토에 보낼 모양 — 화면 모델의 칸 그대로(서버 inputFor 가 화이트리스트로 다시 거른다) */
export function reviewInputOf(form, models) {
  return {
    challengeNm: form.challengeNm, kind: form.kind, dbms: form.dbms, level: form.challengeLevelDcd,
    quizzes: models.map(m => ({
      quizTcd: m.quizTcd, quizDc: m.quizDc, quizHint: m.quizHint, answerSql: m.cransSqlCn,
      choices: m.choices, slowSql: m.slowSql, probeSql: m.probeSql, targetTable: m.targetTable,
      answerIndexesText: m.answerIndexesText,
    })),
  };
}

const EDIT_FIELD_MODEL = { quizDc: 'quizDc', quizHint: 'quizHint', answerSql: 'cransSqlCn' };

/**
 * 검토가 낸 보완을 편집 상태에 얹는다 — 고른 것만. 문항 번호는 **검토할 때의** 1..n 이다(빼기 전에 고치고, 고친 뒤 뺀다).
 * @param picks { edits: [인덱스…], removeQuizNos: [번호…], level: bool, kind: bool }
 * @returns { form, models }
 */
export function applyReviewFixes(form, models, result, picks = {}) {
  const fixes = (result && result.fixes) || {};
  let ms = models.map(m => ({ ...m }));
  (result.edits || []).forEach((e, i) => {
    if (!(picks.edits || []).includes(i)) return;
    const m = ms[e.quizNo - 1];
    const k = EDIT_FIELD_MODEL[e.field];
    if (m && k) ms[e.quizNo - 1] = { ...m, [k]: e.value };
  });
  const drop = new Set((picks.removeQuizNos || []).filter(n => (fixes.removeQuizNos || []).includes(n)));
  if (drop.size && drop.size < ms.length) ms = ms.filter((_, i) => !drop.has(i + 1));
  const next = { ...form };
  if (picks.level && LEVEL_CODES.includes(fixes.level)) next.challengeLevelDcd = fixes.level;
  if (picks.kind && CHALLENGE_KINDS.includes(fixes.kind)) next.kind = fixes.kind;
  return { form: next, models: ms };
}

/**
 * 클라우드 판정(submitChallenge 의 fix · reject) → 검토 패널 모양. 문항 번호는 문자열로 온다 — 숫자로 바꾼다.
 */
export function cloudVerdictToReview(body) {
  const b = body || {};
  const f = b.fixes || {};
  return {
    verdict: b.verdict === 'fix' ? 'fix' : 'reject',
    summary: b.reason || '',
    issues: (b.issues || []).map(x => ({ quizNo: x.quizNo != null && x.quizNo !== '' ? Number(x.quizNo) : null, problem: x.problem || '' })),
    fixes: {
      removeQuizNos: (f.removeQuizNos || []).map(Number).filter(Number.isFinite),
      level: LEVEL_CODES.includes(f.level) ? f.level : null,
      kind: CHALLENGE_KINDS.includes(f.kind) ? f.kind : null,
    },
    edits: [],
  };
}

/**
 * 클라우드 「보완」 을 받아들여 다시 낼 내용 — **제출했던 것**에 보완만 얹는다(QBase fixToken 대조 규칙, 2026-10-06):
 * 지정 번호의 문항만 빼고, level 은 CHALLENGE_LEVEL_DCD, kind 는 속성 JSON 의 kind.
 * 번호: **새 챌린지**(번호 없이 낸 것)는 남은 문항을 순서대로 1..n 으로 다시 매긴다(빈칸이 생기지 않게 — QBase 가 이 형태도 받는다).
 * **기존 챌린지는 다시 매기지 않는다** — 제출 이력이 문항 번호로 이어져 있어 다시 매기면 이력이 다른 문항에 붙는다(QBase 도 받지 않는다).
 * 고른 것만 얹는다 — 일부만 고르면 서버가 조용히 다시 판정한다.
 * @param submitted { header(camelCase), quizzes(교환 형식) } — 직전에 제출한 것
 * @param full      편집 중인 챌린지 전체(교환 형식) — 머리에 없던 속성·수준을 채울 원본
 */
export function applyCloudFix(submitted, full, fixes, picks = {}) {
  const drop = new Set((picks.removeQuizNos || []).filter(n => (fixes.removeQuizNos || []).includes(n)).map(String));
  const kept = (submitted.quizzes || []).filter(q => !drop.has(String(q.quizNo)));
  const quizzes = submitted.id ? kept : kept.map((q, i) => ({ ...q, quizNo: i + 1 }));
  const header = { ...(submitted.header || {}) };
  if (picks.level && fixes.level) header.challengeLevelDcd = fixes.level;
  if (picks.kind && fixes.kind) {
    const attr = parseChallengeAttr(header.challengeAttrCn || (full && full.challengeAttrCn));
    header.challengeAttrCn = JSON.stringify({ ...attr, kind: fixes.kind });
  }
  return { header, quizzes };
}

/** 문항 목록을 교환 형식으로(번호 다시 매김) — 바뀌었는지 견줄 때도 이것을 쓴다 */
export const quizzesToExchange = (models, dbms) => models.map((m, i) => quizToExchange(m, i, dbms));

/**
 * 편집기 전체 검사 — { where: 'header' | 'quizzes' | 'quiz', key, index? } 또는 null.
 * 종류와 맞지 않는 문항 유형도 막는다(쿼리챌린지에 튜닝 실습이 섞이면 목록이 갈린다).
 */
export function validateEditor(form, models, opts = {}) {
  const h = validateHeader(form, opts);
  if (h) return { where: 'header', key: h };
  if (!models.length) return { where: 'quizzes', key: 'noQuiz' };
  if (models.length > MAX_QUIZZES) return { where: 'quizzes', key: 'tooMany' };
  const allowed = QUIZ_TYPES_BY_KIND[form.kind] || QUIZ_TYPES;
  for (let i = 0; i < models.length; i += 1) {
    if (!allowed.includes(models[i].quizTcd)) return { where: 'quiz', key: 'typeKind', index: i };
    const q = validateQuiz(models[i]);
    if (q) return { where: 'quiz', key: q, index: i };
  }
  return null;
}

/**
 * 에이전트 초안(show_challenge_draft → SSE challenge_draft) → 교환 형식 챌린지(편집기 seed).
 * 초안 문항은 편집 모델의 칸 이름을 쓴다 — 저장 형식(cransRstCn)으로 바꾸는 것은 여기서도 quizToExchange 한 곳이다.
 */
const DRAFT_TYPE_TCD = { sql: '001', choice: '002', 'tuning-sql': '003', 'tuning-index': '004' };
export function draftToExchange(d = {}) {
  const kind = CHALLENGE_KINDS.includes(d.kind) ? d.kind : 'query';
  const dbms = CHALLENGE_DBMS.includes(d.dbms) ? d.dbms : 'oracle';
  const models = (Array.isArray(d.quizzes) ? d.quizzes : []).map((q) => {
    const tcd = DRAFT_TYPE_TCD[q.type] || '001';
    const m = newQuiz(tcd);
    return {
      ...m,
      quizDc: q.question || '',
      quizHint: q.hint || '',
      cransSqlCn: q.answerSql || '',
      choices: tcd === '002'
        ? (q.choices || []).map((c, i) => ({ id: String.fromCharCode(97 + i), text: c.text || '', isAnswer: !!c.isAnswer }))
        : m.choices,
      slowSql: q.slowSql || '',
      probeSql: q.probeSql || '',
      targetTable: q.targetTable || '',
      answerIndexesText: (q.answerIndexes || []).map(cols => (cols || []).join(', ')).join('\n'),
      answerUnique: !!q.answerUnique,
      scope: SCOPES.includes(q.scope) ? q.scope : m.scope,
    };
  });
  return {
    challengeNm: d.challengeNm || '',
    challengeLevelDcd: LEVEL_CODES.includes(d.level) ? d.level : '01',
    publicYn: 'N',
    challengeAttrCn: JSON.stringify({ v: 1, kind, dbms, status: 'draft' }),
    quizzes: quizzesToExchange(models, dbms),
  };
}

/** 편집 결과 → 교환 형식의 챌린지 하나(문항 포함) */
export function editorToExchange(form, origHeader, models) {
  return { ...headerToExchange(form, origHeader), quizzes: quizzesToExchange(models, form.dbms) };
}
